import BankModal from "@app/components/bank-modal";
import ConfirmModal from "@app/components/confirm-modal";
import Table from "@app/components/table";
import { ColumnConfig, Order } from "@app/components/table/table.component";
import TitleDescription from "@app/components/title-description";
import { addToast } from "@app/components/toast/toast.service";
import {
  DEBOUNCE_TIMER,
  DEFAULT_PAGE,
  DEFAULT_REQUEST_PARAMS,
  OrderType,
  SystemMessage,
} from "@app/constants";
import BankService from "@app/services/http/bank.service";
import { openPortalDialog } from "@app/services/modal.service";
import { Bank, CreateBank, Meta } from "@app/types";
import { Images } from "@assets/images";
import useCancellationRequest from "@core/hooks/use-cancellation-request.hook";
import { useDebouncedCallback } from "@core/hooks/use-debounced-callback.hook";
import useForceUpdate from "@core/hooks/use-force-update.hook";
import { RequestParams } from "@core/services/http/http.type";
import { useEffect, useState } from "react";

function BankNameColumnTable({ data }: { data: Bank }) {
  return (
    <div className="flex gap-2 items-center truncate min-w-[180px] max-w-[280px]">
      <img className="w-9 h-9" src={data.thumbnail} alt="thumbnail" />
      <div>{data.bankName}</div>
    </div>
  );
}

const columnConfig: ColumnConfig[] = [
  {
    label: "Ngân hàng",
    dataKey: "name",
    width: "30%",
    component: BankNameColumnTable,
    hasOrder: true,
  },
  {
    label: "Chủ tài khoản",
    dataKey: "ownerName",
    width: "35%",
    tdClassName: "truncate min-w-[100px] max-w-[250px]",
    hasOrder: true,
  },
  {
    label: "Số tài khoản",
    dataKey: "bankNumber",
    width: "35%",
    tdClassName: "truncate min-w-[180px] max-w-[280px]",
    hasOrder: true,
  },
];

function BankManagement() {
  const [banks, setBanks] = useState<Bank[]>([]);
  const [requestParams, setRequestParams] = useState<RequestParams>(
    DEFAULT_REQUEST_PARAMS
  );
  const [meta, setMeta] = useState<Meta>();

  const [update, forceUpdate] = useForceUpdate();

  const { abortController } = useCancellationRequest();

  useEffect(() => {
    BankService.getList(requestParams, abortController.signal).then((res) => {
      setBanks(res.data.items);
      setMeta(res.data.meta);

      if (
        requestParams.pageNum &&
        res.data.meta.totalPage > 0 &&
        res.data.meta.totalPage < requestParams.pageNum
      ) {
        const newRequestParams: RequestParams = {
          ...requestParams,
          pageNum: DEFAULT_PAGE,
        };

        setRequestParams(newRequestParams);
      }
    });
  }, [update, requestParams]);

  const onSearchChange = useDebouncedCallback((value: string) => {
    const newRequestParams: RequestParams = {
      ...requestParams,
      keyword: value,
    };

    setRequestParams(newRequestParams);
  }, DEBOUNCE_TIMER);

  const onPageChange = (page: number) => {
    const newRequestParams: RequestParams = { ...requestParams, pageNum: page };

    setRequestParams(newRequestParams);
  };

  const onPageSizeChange = (pageSize: number) => {
    const newRequestParams: RequestParams = { ...requestParams, pageSize };

    setRequestParams(newRequestParams);
  };

  const onOrderChange = (data?: Order) => {
    if (data) {
      const newRequestParams: RequestParams = {
        ...requestParams,
        sortBy: data.orderBy,
        sortType: data.order as OrderType,
      };

      setRequestParams(newRequestParams);
    }
  };

  const handleCreateClick = () => {
    const bankModalObs = openPortalDialog(BankModal);

    bankModalObs.afterClosed().subscribe((data) => {
      if (data?.bank) {
        const createBank: CreateBank = {
          bankName: data.bank.bankName,
          bankNumber: data.bank.bankNumber,
          ownerName: data.bank.ownerName,
          thumbnail: data.bank.thumbnail,
        };

        BankService.create(createBank).then(() => {
          addToast({ text: SystemMessage.ADD_SUCCESSFULLY });
          forceUpdate();
        });
      }
    });
  };

  const handleEditClick = (bank: Bank) => {
    const bankModalObs = openPortalDialog(BankModal, {
      isEdit: true,
      bank,
    });

    bankModalObs.afterClosed().subscribe((data) => {
      if (data?.bank) {
        const createBank: CreateBank = {
          bankName: data.bank.bankName,
          bankNumber: data.bank.bankNumber,
          ownerName: data.bank.ownerName,
          thumbnail: data.bank.thumbnail,
        };

        BankService.update(bank.id, createBank).then(() => {
          addToast({ text: SystemMessage.UPDATE_SUCCESSFULLY });
          forceUpdate();
        });
      }
    });
  };

  const handleDeleteClick = (bankId: number) => {
    if (bankId) {
      const confirmModalObs = openPortalDialog(ConfirmModal, {
        message: SystemMessage.DELETE_BANK_NOTIFY,
      });

      confirmModalObs.afterClosed().subscribe((data) => {
        if (data?.isAccept) {
          BankService.delete(bankId).then(() => {
            addToast({ text: SystemMessage.DELETE_SUCCESSFULLY });
            forceUpdate();
          });
        }
      });
    }
  };

  return (
    <div className="py-6 px-12">
      <div className="flex justify-between items-center">
        <div className="text-[#343C6A]">Danh sách ngân hàng</div>
        <div>
          <button
            className="text-xl px-6 rounded-xl leading-14 h-14 bg-color-primary hover:bg-[#667ffc] text-white"
            onClick={handleCreateClick}
          >
            <div className="flex gap-4">
              <img src={Images.PlusIcon.default} alt="" />
              <div>Tạo mới</div>
            </div>
          </button>
        </div>
      </div>
      <div className="mt-6 rounded-xl bg-white py-6 px-5">
        <TitleDescription
          title="Danh sách ngân hàng"
          onInputChange={onSearchChange}
        />
        <div className="mt-6">
          <Table
            columnConfig={columnConfig}
            dataTable={banks}
            hasCheckBox
            hasAction
            onEditClick={handleEditClick}
            onDeleteClick={handleDeleteClick}
            meta={meta}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            onRowClickOrder={onOrderChange}
            componentEmpty={
              <div className="text-center py-3 bg-[#f4f4f4]">
                <p className="text-[#666] text-xl">Không có dữ liệu</p>
              </div>
            }
          />
        </div>
      </div>
    </div>
  );
}

export default BankManagement;
